(function () {
  'use strict';

  if (window.VariantPickerSliderInitialized) return;
  window.VariantPickerSliderInitialized = true;

  class VariantPickerSlider {
    constructor() {
      this.swiperInstances = new Map(); // id -> swiper instance
      this.refreshTimer    = null;
      this.init();
    }

    /* ─────────────────────────────────────────────
       BOOT
    ───────────────────────────────────────────── */
    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.waitForSwiper());
      } else {
        this.waitForSwiper();
      }
      this.bindGlobalEvents();
      this.interceptReloads();
    }

    waitForSwiper() {
      if (typeof Swiper !== 'undefined') {
        this.setup();
      } else {
        setTimeout(() => this.waitForSwiper(), 50);
      }
    }

    setup() {
      // first paint
      this.scheduleRefresh(0);

      // watch for new slider elements added to DOM (Quick View, variant re-render etc.)
      const mo = new MutationObserver((mutations) => {
        const added = mutations.some(m =>
          Array.from(m.addedNodes).some(n =>
            n.nodeType === 1 && (
              n.matches?.('[data-swiper-variant-picker]') ||
              n.querySelector?.('[data-swiper-variant-picker]')
            )
          )
        );
        if (added) this.scheduleRefresh(0); // immediate — DOM is already there
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    /* ─────────────────────────────────────────────
       STATE
    ───────────────────────────────────────────── */
    saveState(optionName, value) {
      (window._vpSavedStates = window._vpSavedStates || {})[optionName] = value;
    }

    getState(optionName) {
      return window._vpSavedStates?.[optionName] ?? null;
    }

    /* ─────────────────────────────────────────────
       INTERCEPT VARIANT / SECTION RELOADS
    ───────────────────────────────────────────── */
    interceptReloads() {
      // Shopify section reload
      document.addEventListener('shopify:section:load', () => this.scheduleRefresh(0));

      // Save which option was clicked BEFORE the theme re-renders the DOM.
      // Using capture:true so we run before any other handler.
      document.addEventListener('change', (e) => {
        if (!e.target.matches('.variant-swatch-input, .drawer-swatch-input')) return;
        const optionName =
          e.target.closest('[data-option-name]')?.dataset.optionName || e.target.name;
        if (optionName) this.saveState(optionName, e.target.value);
      }, true);
    }

    /* ─────────────────────────────────────────────
       REFRESH SCHEDULER
       delay=0  → next microtask (instant)
       delay=N  → debounced by N ms
    ───────────────────────────────────────────── */
    scheduleRefresh(delay = 20) {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), delay);
    }

    refresh() {
      this.moveDrawersToBody();
      this.initSwipers();
    }

    /* ─────────────────────────────────────────────
       SWIPER INIT
    ───────────────────────────────────────────── */
    cleanString(str) {
      return str ? String(str).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
    }

    resetScrollbar(swiperEl) {
      // Clear stale inline styles left by a previous Swiper instance so the
      // drag-handle width is always recalculated from scratch on re-init.
      const sbId = swiperEl.dataset.scrollbarEl;
      if (!sbId) return;
      const drag = document.getElementById(sbId)?.querySelector('.swiper-scrollbar-drag');
      if (drag) {
        drag.style.cssText = ''; // wipe ALL inline styles at once
      }
    }

    initSwipers() {
      const els = document.querySelectorAll('[data-swiper-variant-picker]');

      els.forEach((swiperEl) => {
        const id = swiperEl.id;

        // ── Destroy stale instance if element was replaced by a theme re-render ──
        // A re-rendered element is NOT .swiper-initialized even if we have a stored
        // instance for that id — destroy the old one so we can re-init cleanly.
        if (this.swiperInstances.has(id)) {
          if (!swiperEl.classList.contains('swiper-initialized')) {
            // DOM was replaced; old instance points to detached node — just delete it
            this.swiperInstances.delete(id);
          } else {
            // Already live — nothing to do
            return;
          }
        } else if (swiperEl.classList.contains('swiper-initialized')) {
          // Swiper was init'd by something else; register it and move on
          if (swiperEl.swiper) this.swiperInstances.set(id, swiperEl.swiper);
          return;
        }

        // ── Find the correct starting slide ──
        const optionName  = swiperEl.dataset.optionName;
        let   activeValue = this.getState(optionName)
                          ?? swiperEl.querySelector('input:checked')?.value
                          ?? null;

        const slides      = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
        let   startIndex  = 0;

        if (activeValue) {
          const cleanActive = this.cleanString(activeValue);
          const target = slides.find(s =>
            s.dataset.value === activeValue ||
            this.cleanString(s.dataset.value) === cleanActive
          );
          if (target) {
            startIndex = slides.indexOf(target);
            slides.forEach(s => s.classList.remove('active-swatch'));
            target.classList.add('active-swatch');
          }
        }

        // ── Reset scrollbar BEFORE Swiper touches it ──
        this.resetScrollbar(swiperEl);

        try {
          const swiper = new Swiper(swiperEl, {
            initialSlide      : startIndex,
            speed             : 120,        // fast but visibly smooth
            slidesPerView     : 'auto',
            spaceBetween      : 10,
            centeredSlidesBounds: true,
            threshold         : 5,
            // observer / observeParents let Swiper self-heal if its container is
            // resized (e.g. sticky sidebar, tab panels) — keep these ON.
            observer          : true,
            observeParents    : true,
            // ── Scrollbar ──
            scrollbar: {
              el      : `#${swiperEl.dataset.scrollbarEl}`,
              draggable: true,
            },
            // ── Arrows ──
            navigation: {
              nextEl: `#${swiperEl.dataset.nextEl}`,
              prevEl: `#${swiperEl.dataset.prevEl}`,
            },
            on: {
              // afterInit fires synchronously inside new Swiper() before we return,
              // so the scrollbar size is correct from the very first frame.
              afterInit(sw) {
                if (sw.scrollbar?.el) {
                  sw.scrollbar.updateSize();
                  sw.scrollbar.setTranslate();
                }
              },
              // Also update on resize so the bar never gets stuck at a wrong width.
              resize(sw) {
                if (sw.scrollbar?.el) {
                  sw.scrollbar.updateSize();
                  sw.scrollbar.setTranslate();
                }
              },
            },
          });

          this.swiperInstances.set(id, swiper);

        } catch (err) {
          console.error('[VP-Slider] Swiper init failed:', err);
          // Make sure the element is visible even if Swiper failed
          swiperEl.style.opacity    = '1';
          swiperEl.style.visibility = 'visible';
        }
      });
    }

    /* ─────────────────────────────────────────────
       DRAWER PORTAL
       Move drawers to <body> so z-index / overflow
       clipping from the product form can't hide them.
    ───────────────────────────────────────────── */
    moveDrawersToBody() {
      document.querySelectorAll('[data-drawer]').forEach((drawer) => {
        if (drawer.parentElement !== document.body) {
          document.body.appendChild(drawer);
        }
      });
    }

    /* ─────────────────────────────────────────────
       UI HELPERS
    ───────────────────────────────────────────── */
    updateLabel(optionName, cleanName) {
      const el = document.querySelector(`[data-selected-swatch-value="${optionName}"]`);
      if (el) el.textContent = cleanName;
    }

    /* ─────────────────────────────────────────────
       GLOBAL EVENTS  (swatch clicks / drawer)
    ───────────────────────────────────────────── */
    bindGlobalEvents() {

      // ── Swatch change (slider) ──
      document.body.addEventListener('change', (e) => {

        /* --- SLIDER SWATCH --- */
        if (e.target.classList.contains('variant-swatch-input')) {
          const slide      = e.target.closest('.swiper-slide');
          const cleanName  = slide?.dataset.cleanName || e.target.value;
          const optionName = e.target.closest('[data-option-name]')?.dataset.optionName;
          if (optionName) this.updateLabel(optionName, cleanName);
        }

        /* --- DRAWER SWATCH --- */
        if (e.target.classList.contains('drawer-swatch-input')) {
          const value      = e.target.value;
          const formId     = e.target.getAttribute('form');
          const cleanName  = e.target.closest('[data-drawer-swatch]')?.dataset.cleanName || value;

          // Sync the corresponding slider radio
          const mainInput  = document.querySelector(
            `.swiper-variant-picker input[value="${CSS.escape(value)}"][form="${formId}"]`
          );

          if (mainInput) {
            mainInput.checked = true;
            mainInput.dispatchEvent(new Event('change', { bubbles: true }));

            const swiperContainer = mainInput.closest('.swiper');
            const slide           = mainInput.closest('.swiper-slide');
            const optionName      = swiperContainer?.dataset.optionName;

            if (optionName) this.updateLabel(optionName, cleanName);

            if (slide && swiperContainer) {
              const sw    = this.swiperInstances.get(swiperContainer.id);
              const index = Array.from(swiperContainer.querySelectorAll('.swiper-slide')).indexOf(slide);
              if (sw) sw.slideTo(index, 120);
            }

            // Close drawer after a short visual pause
            setTimeout(() => {
              const drawer = e.target.closest('.variant-drawer');
              if (drawer) {
                drawer.classList.remove('is-open');
                document.body.classList.remove('no-scroll');
              }
            }, 250);
          }
        }
      });

      // ── Drawer open / close / accordion ──
      document.body.addEventListener('click', (e) => {

        // Open drawer
        const trigger = e.target.closest('[data-drawer-trigger]');
        if (trigger) {
          e.preventDefault();
          const drawer = document.getElementById(trigger.dataset.drawerTrigger);
          if (drawer) {
            drawer.classList.add('is-open');
            document.body.classList.add('no-scroll');
          }
          return;
        }

        // Close drawer (X button or overlay)
        if (e.target.closest('[data-drawer-close]') ||
            e.target.classList.contains('variant-drawer__overlay')) {
          const drawer = e.target.closest('.variant-drawer');
          if (drawer) {
            drawer.classList.remove('is-open');
            document.body.classList.remove('no-scroll');
          }
          return;
        }

        // Accordion inside drawer
        const accordionBtn = e.target.closest('[data-accordion-trigger]');
        if (accordionBtn) {
          e.preventDefault();
          const group   = accordionBtn.closest('[data-accordion-group]');
          const content = group?.querySelector('[data-accordion-content]');
          group?.classList.toggle('drawer-accordion-group--open');
          content?.classList.toggle('drawer-accordion-content--open');
        }
      });
    }
  }

  new VariantPickerSlider();
})();